import React, { useState, useEffect, useRef } from "react";
import moment from "moment";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";

import Wizard from "./Wizard";
import Attachment from "./Component/Attachment";
import Risks, { risksValidationSchema } from "./Component/Risks";
import Details, { detailsValidationSchema } from "./Component/Details";
import Results, { resultsValidationSchema } from "./Component/Results";

import api from "../../providers/api";
import { addToast } from "../../helpers";

import "./style.scss";

function ObjectiveWizard(props) {
  const intl = useIntl();
  const [strategyTypes, objectiveLevels] = useSelector(({ theme, user }) => [
    theme.StrategyTypes.item.Items,
    theme?.ObjectiveLevel?.item?.Items,
    user.user
  ]);
  const formRef = useRef();

  const [lists, setLists] = useState({
    strategiesWithThemes: [],
    strategiesWithPillars: [],
    perspectives: [],
    users: [],
    orgStructure: [],
    loading: {
      strategiesWithThemes: true,
      strategiesWithPillars: true,
      perspectives: true,
      users: true,
      orgStructure: true
    }
  });
  const [state, setState] = useState({
    ArabicName: "",
    EnglishName: "",
    EnglishDescription: "",
    ArabicDescription: "",
    StrategyType: props?.selectedType || strategyTypes[0],
    Strategy: props?.selectedStrategy || null,
    Perspective: null,
    Theme: null,
    Pillar: null,
    Level: null,
    ParentID: null,
    StartDate: null,
    ObjectiveSectors: [],
    ObjectiveExecutiveDepartments: [],
    ObjectiveDepartments: [],
    Order: "",
    Code: "",
    ObjectiveResults: [],
    ObjectiveRisks: [],
    Attachments: []
  });

  async function getObjectiveById(lists) {
    const { editData, modeSaveOrUpdate } = props;
    const requests = [];

    if (modeSaveOrUpdate === false) {
      requests.push(api.objective.getById(editData?.ID));
    }

    const [res] = await Promise.all(requests);

    if (modeSaveOrUpdate) {
      const { current } = formRef;
      if (current) {
      }
    }
    if (res?.StatusCode === "Success") {
      const Data = res?.Data;
      const isStrategic = +Data?.StrategyType === 1;
      const strategiesList = isStrategic
        ? lists.strategiesWithThemes
        : lists.strategiesWithPillars;
      const strategy = strategiesList?.find(
        (e) =>
          e.ID === Data?.Theme?.Strategy?.ID ||
          e.ID === Data?.Pillar?.Strategy?.ID
      );

      const theme = isStrategic
        ? strategy?.Themes?.find((e) => e.ID === Data?.Theme?.ID)
        : null;

      const pillar = isStrategic
        ? null
        : strategy?.Pillars?.find((e) => e.ID === Data?.Pillar?.ID);

      const sectors = Data?.ObjectiveSectors?.length
        ? Data?.ObjectiveSectors?.map((e) =>
            lists?.orgStructure?.find((item) => item.ID === e.ID)
          )
        : [];

      const executiveDepartmentsList = lists.orgStructure?.flatMap(
        (e) => e.Childrens
      );
      const executiveDepartments = Data?.ObjectiveExecutiveDepartments?.length
        ? Data?.ObjectiveExecutiveDepartments?.map((e) =>
            executiveDepartmentsList?.find((item) => item.ID === e.ID)
          )
        : [];

      setState({
        ...Data,
        StrategyType: strategyTypes.find(
          (item) => +item.Value === +Data.StrategyType
        ),
        Level: objectiveLevels.find((item) => +item.Value === +Data.Level),
        ParentID: Data?.Parent || null,
        Strategy: strategy,
        Theme: theme,
        Pillar: pillar,
        StartDate: Data.StartDate ? new Date(Data.StartDate) : null,
        ObjectiveSectors: sectors,
        ObjectiveExecutiveDepartments: executiveDepartments,
        ObjectiveResults: Data?.ObjectiveResults?.map((e, i) => {
          return { ...e, index: i };
        }),
        ObjectiveRisks: Data?.ObjectiveRisks?.map((e, i) => {
          return { ...e, index: i };
        })
      });
    }
  }

  async function getLists() {
    const { modeSaveOrUpdate } = props;
    const { current } = formRef;
    const [
      usersRes,
      strategiesWithThemesRes,
      strategiesWithPillarsRes,
      perspectivesRes,
      orgStructureRes,
      allRelatedObjectivesRes
    ] = await Promise.all([
      api.user.getAll(),
      api.strategy.getAllWithObjectives({ strategyType: 1 }),
      api.strategy.getAllWithPillars(),
      api.perspective.getAll(),
      api.orgStructure.getAllTree(),
      api.objective.getObjectivesByStrategy()
    ]);

    const newLists = {
      ...lists,
      strategiesWithThemes:
        strategiesWithThemesRes.StatusCode === "Success"
          ? strategiesWithThemesRes.Data
          : [],
      strategiesWithPillars:
        strategiesWithPillarsRes.StatusCode === "Success"
          ? strategiesWithPillarsRes.Data
          : [],
      perspectives:
        perspectivesRes.StatusCode === "Success" ? perspectivesRes.Data : [],
      users: usersRes.StatusCode === "Success" ? usersRes.Data : [],
      orgStructure:
        usersRes.StatusCode === "Success" ? orgStructureRes.Data : [],
      relatedObjectives:
        allRelatedObjectivesRes.StatusCode === "Success"
          ? allRelatedObjectivesRes?.Data?.map((e) => ({
              ...e,
              Objectives: e?.Objectives?.filter((item) => item.Level === 1)
            }))
          : [],
      loading: {
        ...lists.loading,
        users: false,
        strategiesWithThemes: false,
        strategiesWithPillars: false,
        perspectives: false,
        orgStructure: false
      }
    };
    setLists(newLists);

    if (modeSaveOrUpdate && current) {
      current.setFieldValue("OwnerModel", usersRes.Data?.[0]);
    }

    return newLists;
  }

  async function fetchData() {
    const currentLists = await getLists();
    getObjectiveById(currentLists);
  }

  useEffect(() => {
    fetchData();
  }, []);

  const { closeModal } = props;
  const { ID } = state;

  async function handleSubmit(values, formBag) {
    const { editData, closeModal, bindObjective } = props;
    const body = {
      ...values,
      ParentID: values?.ParentID?.ID,
      Level: values?.Level?.Value,
      StrategyType: values?.StrategyType?.Value,
      StartDate: values.StartDate
        ? moment(values.StartDate).locale("en").format("MM-DD-YYYY")
        : null
    };

    try {
      if (body.Attachments?.length) {
        const oldAttachments = [];
        const newAttachments = [];
        body.Attachments?.forEach((item) =>
          item.ID ? oldAttachments.push(item) : newAttachments.push(item)
        );
        if (newAttachments?.length) {
          const attachments = await api.attachment.create(newAttachments);
          body.Attachments = [
            ...oldAttachments,
            ...attachments?.Data?.map((item) => ({ ID: item }))
          ];
        }
      }
      const action = editData?.ID ? "update" : "create";
      const res = await api.objective[action](body);
      if (res.StatusCode === "Success") {
        addToast.success(
          editData?.ID ? "toast.successUpdate" : "toast.successSave"
        );
        if (bindObjective) bindObjective(res.Data);
        closeModal();
        if (props?.getData) props?.getData();
      }
    } catch (error) {
      // addToast.error(error.message, {
      //   toastId: "get-users-error"
      // });
    } finally {
      formBag.setSubmitting(false);
    }
  }

  return (
    <div className="objective-wizard-container">
      <Wizard
        ref={formRef}
        closeModal={closeModal}
        key={ID}
        initialValues={state}
        onSubmit={handleSubmit}
        isReadOnly={props?.isReadOnly}
        setIsDraft={() => {}}
      >
        <Details
          name={intl.formatMessage({ id: "objective.objective-details" })}
          validationSchema={detailsValidationSchema}
          lists={lists}
          isEditMode={!!props.editData?.ID}
          details={props?.editData}
        />
        <Results
          name={intl.formatMessage({ id: "objective.results" })}
          validationSchema={resultsValidationSchema}
          isEditMode={!!props.editData?.ID}
        />

        <Risks
          name={intl.formatMessage({ id: "objective.risks" })}
          validationSchema={risksValidationSchema}
          isEditMode={!!props.editData?.ID}
        />

        <Attachment
          name={intl.formatMessage({ id: "common.attachments" })}
          isReadOnly={props?.isReadOnly}
        />
      </Wizard>
    </div>
  );
}

export default ObjectiveWizard;
